import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { subscriptionService, type SubscriptionTier, type MachineTypeQuotaSnapshot } from '../services/subscriptionService';
import { useAuthStore } from '../store/authStore';
import { useSubscriptionStore } from '../store/subscriptionStore';

// Base key — used for invalidation. The actual query key includes the user id
// so switching accounts (logout → login as a different user) does NOT serve
// the previous user's cached subscription state.
export const SUBSCRIPTION_QUERY_KEY = ['subscription'] as const;

export interface RoastQuotaUI {
  tier: SubscriptionTier;
  limitKg: number;
  usedKg: number;
  remainingKg: number;
  cycleStart: Date;
  cycleEnd: Date;
}

export function useSubscription() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const setData = useSubscriptionStore((s) => s.setData);

  const query = useQuery({
    queryKey: [...SUBSCRIPTION_QUERY_KEY, userId],
    queryFn: subscriptionService.getMine,
    enabled: isAuthenticated && !!userId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    setData(query.data ?? null);
  }, [query.data, setData]);

  const data = query.data;
  // PRD 76: optimistic default is 'basic' — never lock UI before first load.
  const tier: SubscriptionTier = data?.tier ?? 'basic';
  const roastQuota: RoastQuotaUI | null = data?.roast_quota
    ? {
        tier: data.roast_quota.tier,
        limitKg: data.roast_quota.limit_kg,
        usedKg: data.roast_quota.used_kg,
        remainingKg: data.roast_quota.remaining_kg,
        cycleStart: new Date(data.roast_quota.cycle_start),
        cycleEnd: new Date(data.roast_quota.cycle_end),
      }
    : null;
  const machineTypeQuota: Pick<MachineTypeQuotaSnapshot, 'used' | 'active' | 'limit' | 'remaining'> | null =
    data?.machine_type_quota
      ? {
          used: data.machine_type_quota.used,
          active: data.machine_type_quota.active,
          limit: data.machine_type_quota.limit,
          remaining: data.machine_type_quota.remaining,
        }
      : null;
  return {
    ...query,
    tier,
    expiresAt: data?.expires_at ? new Date(data.expires_at) : null,
    daysRemaining: data?.days_remaining ?? null,
    isFree: data?.tier === 'free',
    isBasic: data?.tier === 'basic',
    isPlus: data?.tier === 'plus',
    isPaid: tier === 'basic' || tier === 'plus',
    roastQuota,
    machineTypeQuota,
  };
}
